// SPDX-License-Identifier: AGPL-3.0-or-later

import type {Channel} from '@app/features/channel/models/Channel';
import {lastActivityId} from '@app/features/threads/state/ChannelThreads';
import {
	ChannelFlags,
	type ForumSortOrderType,
	ForumSortOrderTypes,
	type ForumTagSetting,
	ForumTagSettings,
} from '@fluxer/constants/src/ThreadConstants';
import * as SnowflakeUtils from '@fluxer/snowflake/src/SnowflakeUtils';

export const FORUM_GRID_GAP_PX = 12;
const FORUM_GRID_CARD_MIN_WIDTH_PX = 240;

export function isPinnedPost(post: Channel): boolean {
	return (post.flags & ChannelFlags.PINNED) !== 0;
}

export function postSortKey(post: Channel, sortOrder: ForumSortOrderType): string {
	return sortOrder === ForumSortOrderTypes.CREATION_TIME ? post.id : lastActivityId(post);
}

export function matchesTagFilter(
	appliedTags: ReadonlyArray<string>,
	tagIds: ReadonlyArray<string>,
	tagSetting: ForumTagSetting,
): boolean {
	if (tagIds.length === 0) return true;
	if (tagSetting === ForumTagSettings.MATCH_ALL) return tagIds.every((tagId) => appliedTags.includes(tagId));
	return tagIds.some((tagId) => appliedTags.includes(tagId));
}

export interface ForumPostListInput {
	known: ReadonlyArray<Channel>;
	extra: ReadonlyArray<Channel>;
	sortOrder: ForumSortOrderType;
	tagIds: ReadonlyArray<string>;
	tagSetting: ForumTagSetting;
}

export interface ForumPostListResult {
	pinned: Channel | null;
	active: Array<Channel>;
	archived: Array<Channel>;
}

export function buildForumPostList({
	known,
	extra,
	sortOrder,
	tagIds,
	tagSetting,
}: ForumPostListInput): ForumPostListResult {
	const seen = new Set<string>();
	const activePosts: Array<Channel> = [];
	const archivedPosts: Array<Channel> = [];
	let pinned: Channel | null = null;
	for (const post of [...known, ...extra]) {
		if (seen.has(post.id)) continue;
		seen.add(post.id);
		if (!matchesTagFilter(post.appliedTags, tagIds, tagSetting)) continue;
		if (isPinnedPost(post) && !post.isArchived && pinned == null) {
			pinned = post;
			continue;
		}
		(post.isArchived ? archivedPosts : activePosts).push(post);
	}
	const byKey = (a: Channel, b: Channel) =>
		SnowflakeUtils.compare(postSortKey(b, sortOrder), postSortKey(a, sortOrder));
	activePosts.sort(byKey);
	archivedPosts.sort(byKey);
	return {pinned, active: activePosts, archived: archivedPosts};
}

export type ForumPostRow = {type: 'posts'; key: string; posts: ReadonlyArray<Channel>} | {type: 'older'; key: 'older'};

// A single-post row moves with its post. A gallery row keeps its place, so only a card that leaves the row remounts.
function chunkPosts(
	posts: ReadonlyArray<Channel>,
	columns: number,
	section: 'current' | 'archived',
): Array<ForumPostRow> {
	const rows: Array<ForumPostRow> = [];
	for (let index = 0; index < posts.length; index += columns) {
		const key = columns === 1 ? posts[index]!.id : `posts:${section}:${index / columns}`;
		rows.push({type: 'posts', key, posts: posts.slice(index, index + columns)});
	}
	return rows;
}

export function buildForumPostRows(
	current: ReadonlyArray<Channel>,
	archived: ReadonlyArray<Channel>,
	columns: number,
): Array<ForumPostRow> {
	const older: Array<ForumPostRow> = archived.length > 0 ? [{type: 'older', key: 'older'}] : [];
	return [...chunkPosts(current, columns, 'current'), ...older, ...chunkPosts(archived, columns, 'archived')];
}

// The column count of the gallery's auto-fill grid (.postGrid in Forum.module.css), which still lays out each row.
export function getForumGridColumns(width: number, remScale: number): number {
	const gap = FORUM_GRID_GAP_PX * remScale;
	return Math.max(1, Math.floor((width + gap) / (FORUM_GRID_CARD_MIN_WIDTH_PX * remScale + gap)));
}
